/**
 * Data model — spec section 3.
 *
 * Ratings are stored at full precision and rounded only for display. Section 10
 * says not to show decimals; it does not say to throw the fractions away, and
 * keeping them stops a systematic rounding drift over a season of games.
 */

export type Color = 'W' | 'U' | 'B' | 'R' | 'G';

export const COLORS: readonly Color[] = ['W', 'U', 'B', 'R', 'G'];

export type PowerLevel = 1 | 2 | 3 | 4 | 5;

export const POWER_LEVELS: readonly PowerLevel[] = [1, 2, 3, 4, 5];

const ROMAN = ['I', 'II', 'III', 'IV', 'V'] as const;

/** Brackets read as numerals, so they never look like a rating. */
export const roman = (level: PowerLevel): string => ROMAN[level - 1]!;

export function isPowerLevel(value: unknown): value is PowerLevel {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 5;
}

export interface Pilot {
  id: string;
  name: string;
  /** The rating on their card: what they have won plus what they have earned. */
  elo: number;
  /**
   * How much of `elo` was paid for turning up and for takedowns.
   *
   * The league inflates on purpose — twenty game nights ought to show — but a
   * model that reads that inflation as skill would rate the person who comes
   * every week above the equally good person who comes monthly, and would rate
   * anybody who joined this month as hopeless. So the paid half is tracked
   * here, and `skillOf` subtracts it back out before any prediction is made.
   *
   * Missing on members who predate the split, which reads as zero — correct,
   * because nothing had been paid separately yet.
   */
  progress?: number;
  placementGames: number;
  /** Soft-delete so historical matches keep resolving to a name. */
  retired: boolean;
  createdAt: number;
  updatedAt: number;

  /**
   * The Cognito subject of the account that registered as this member, or null
   * for a guest somebody added by hand. This is what lets a person sign in and
   * find themselves already in the guild rather than being asked to register
   * twice — and it is set from the verified token server-side, never from the
   * request body.
   */
  sub: string | null;

  /**
   * The address on the account, read from Cognito rather than the request.
   *
   * Null for a guest nobody has signed in as. Visible to the rest of the guild,
   * which is no new exposure — you are invited by email, and the access list
   * already shows those to every member.
   */
  email: string | null;

  /**
   * Guild leaders can remove members and promote others. Everyone can invite.
   *
   * There is always at least one: the first person to register becomes a
   * leader, and the last one cannot be demoted, or the guild would end up with
   * nobody able to grant the role back.
   */
  leader: boolean;

  /**
   * A 128px square JPEG as a data URI, or null for the generated initials.
   *
   * Stored inline rather than in S3: at roughly 8KB an avatar is a rounding
   * error against DynamoDB's 400KB item limit, it arrives with the single
   * Query that already loads the league, and it works offline for free. A
   * bucket, presigned uploads and a CDN path would be a lot of moving parts
   * for six thumbnails.
   */
  avatar: string | null;
}

export interface Deck {
  id: string;
  /** The commander, as Scryfall spells it. Never edited by hand. */
  name: string;
  /**
   * What the table calls it, if that is not the commander's name.
   *
   * Two people can bring Atraxa and mean entirely different decks, and a group
   * that has been playing together a while has a name for each of them. Kept
   * separate from the commander rather than overwriting it, because the
   * commander is what the art, the colours and the Scryfall link all hang off —
   * lose it and the deck stops being able to look itself up.
   */
  nickname: string | null;
  colors: Color[];
  ownerId: string | null;

  /**
   * Bracket, I to V, or null if nobody has said.
   *
   * Purely descriptive: it is what the table agrees the deck is built to, and
   * it never touches the rating. The rating is the measurement — letting a
   * self-declared number feed into it would be measuring the claim instead.
   */
  powerLevel: PowerLevel | null;
  scryfallId: string | null;
  artCropUrl: string | null;

  /**
   * The second commander, for a deck built on a partner pair.
   *
   * A whole second card rather than a name, because the colour identity of the
   * deck is the union of the two and the banner draws both pieces of art. Null
   * for the overwhelming majority of decks, which have one commander.
   *
   * The pair is unordered at the table but stored in the order it was entered,
   * and the first one keeps the primary slot — it is what the deck sorts and
   * searches by when there is no nickname.
   */
  partner?: {
    name: string;
    scryfallId: string | null;
    artCropUrl: string | null;
  } | null;

  /**
   * The primary commander's own colours, kept only while a partner is attached.
   *
   * `colors` is the union of the pair, because they share a card pool. Taking a
   * partner off again has to put the deck back to what it was, and a union
   * cannot be undone — Thrasios and Tymna make four colours with no way to tell
   * afterwards which two belonged to which. Recorded when the partner is added
   * and read when it is removed.
   *
   * Absent on every deck that has never had one, where `colors` is already the
   * answer.
   */
  soloColors?: Color[];
  /**
   * Art for a deck that is not a Scryfall card, as a data URI.
   *
   * Custom decks — a precon somebody rebadged, a cube deck, a commander from a
   * set the database has not caught up with — have no card to look up and no
   * art to fetch. Stored inline rather than in the local blob store, because
   * the blob store is one device: a picture only the phone that uploaded it can
   * see is barely a picture at all in a guild of four.
   *
   * Budgeted like an avatar and resized on the device before upload, since the
   * whole league arrives in one request and DynamoDB stops at 400KB an item.
   */
  customArt?: string | null;

  /** Whether the art bytes are held in the local IndexedDB blob store. */
  artCached: boolean;
  elo: number;
  peakElo: number;
  placementGames: number;
  retired: boolean;
  createdAt: number;
  updatedAt: number;
  /** Spec 5, tier 3 — lets you see whether a rebuild actually helped. */
  lastRebuiltAt: number | null;

  /**
   * Every time the table changed its mind about this deck's bracket.
   *
   * The bracket is a live field, so without this there is no way to know which
   * games a deck played as a III and which as a IV — and the bracket is not
   * decoration: it handicaps its pilot's rating against lower-bracket
   * opponents, so it is a term in how every one of those games was scored.
   *
   * Only changes are recorded. What it was registered as is the `from` of the
   * first entry, or the current bracket if it has never moved, so the whole
   * history reconstructs from this plus the field.
   *
   * Absent on decks that predate it, and on decks nobody has reclassified.
   */
  bracketHistory?: { at: number; from: PowerLevel | null; to: PowerLevel | null }[];
}

/**
 * What to call a deck on screen: its nickname, or the commander if it has none.
 *
 * Everywhere, so the app never says "Atraxa, Praetors' Voice" to a table that
 * has called it Superfriends since March.
 */
/**
 * What to call a deck on screen.
 *
 * A nickname wins, as it always did — that is the whole point of having one,
 * and a partner deck is exactly the kind that gets one, because two commanders'
 * worth of name does not fit anywhere. Failing that, both commanders, joined
 * the way a table says them out loud.
 */
/**
 * How a pair is written out, matching the diagonal the art is split down.
 *
 * A slash rather than a plus. Two commanders are not a sum — they are one deck
 * seen from two sides, which is exactly what the banner says by cutting the
 * art in half at the same angle.
 */
export const PAIR_JOIN = ' // ';

export const deckLabel = (deck: {
  name: string;
  nickname?: string | null;
  partner?: { name: string } | null;
}): string => {
  const nickname = deck.nickname?.trim();
  if (nickname) return nickname;
  return deck.partner?.name ? `${deck.name}${PAIR_JOIN}${deck.partner.name}` : deck.name;
};

/** Just the commanders, for the places that want the cards and not the name. */
export const commandersOf = (deck: { name: string; partner?: { name: string } | null }): string[] =>
  deck.partner?.name ? [deck.name, deck.partner.name] : [deck.name];

export const MAX_DECK_NICKNAME = 40;

export interface MatchEntry {
  pilotId: string;
  deckId: string;
}

/**
 * How the game actually ended, in the words a table uses at the table.
 *
 * "Attrition" was the old name for combat damage and nobody could tell what it
 * meant; "combo" did not say infinite. The point of this field is that somebody
 * picks the true one in the ten seconds after a game, and a label you have to
 * decode gets whatever is nearest instead.
 */
export type WinCondition =
  | 'combat damage'
  | 'commander damage'
  | 'infinite combo'
  | 'poison'
  | 'alternative win'
  | 'concede';

/**
 * Values written before the rename, still on stored matches.
 *
 * Carried in the type rather than migrated: two dead strings cost nothing, and
 * rewriting recorded history to tidy up a label is the worse trade.
 */
export type LegacyWinCondition = 'combo' | 'attrition';

/**
 * The picker's order — commonest first, so the usual answer is the short reach.
 *
 * "Alternative win" sits below the four damage answers and above concede: it is
 * the rarest of the real ones, and it is not a way of dealing damage at all.
 * Thassa's Oracle, Approach of the Second Sun, Coalition Victory — the cards
 * that say you win the game, full stop. Filing those under "infinite combo"
 * was the nearest available lie and it hid the one thing worth knowing about
 * a deck that owns one.
 */
export const WIN_CONDITIONS: readonly WinCondition[] = [
  'combat damage',
  'commander damage',
  'infinite combo',
  'poison',
  'alternative win',
  'concede',
];

const WIN_CONDITION_LABELS: Record<WinCondition | LegacyWinCondition, string> = {
  'combat damage': 'Combat damage',
  'commander damage': 'Commander damage',
  'infinite combo': 'Infinite combo',
  poison: 'Poison',
  'alternative win': 'Alternative win',
  concede: 'Concede',
  // Shown as what they always meant.
  combo: 'Infinite combo',
  attrition: 'Combat damage',
};

export const winConditionLabel = (value: WinCondition | LegacyWinCondition): string =>
  WIN_CONDITION_LABELS[value] ?? value;

/**
 * Whether a game counts for anything.
 *
 * The one place that decides it. Every function that builds a record, a rate, a
 * streak, a rating curve or a night out of the match log asks this first, so
 * that "a cancelled game is worth nothing" is a single sentence in the codebase
 * rather than a rule each of twenty callers is trusted to remember.
 */
export const isRated = (m: Pick<Match, 'cancelled'>): boolean => !m.cancelled;

import type { CommentaryPost } from './commentary.js';

/** Deltas applied by a match, stored so undo is exact rather than recomputed. */
export interface MatchDeltas {
  /** The whole change to each guildmate's rating: won plus paid. */
  pilots: Record<string, number>;
  decks: Record<string, number>;
  /**
   * The paid half — attendance and takedowns — kept apart so it can be
   * subtracted back out to recover skill, and so undo restores both.
   *
   * Absent on matches logged before the split, where nothing was paid
   * separately and zero is the right answer.
   */
  progress?: Record<string, number>;
}

export interface Match {
  id: string;
  ts: number;
  entries: MatchEntry[];
  /**
   * Who took it, or null for a game that was cancelled rather than won.
   *
   * Nullable rather than an empty string so that every reader has to say what
   * it does about a game nobody won. An empty string would compare false
   * against every seat and quietly report the whole table as having lost.
   */
  winnerPilotId: string | null;
  sessionId: string | null;

  /**
   * The table met, played, and the game did not resolve into a result.
   *
   * A misdeal, a rules argument nobody could settle, somebody having to leave
   * - the evening happened and the game did not. It is logged so the night
   * reads correctly in History and so nobody wonders whether they forgot to
   * write it down, and it is worth nothing: no deltas, no attendance, no
   * placement game, no entry in anybody's record. `isRated` is the one place
   * that decides this, and everything that counts games asks it.
   */
  cancelled?: true;

  endTurn?: number;
  onThePlay?: string;
  mulligans?: Record<string, number>;
  winCondition?: WinCondition | LegacyWinCondition;
  /**
   * How the table was filled: standard, or a random draft.
   *
   * Absent on every game logged before the draft existed and on anything typed
   * in by hand, both of which are standard — so it is read through a default
   * rather than compared, and a missing value never means "unknown format".
   */
  mode?: 'standard' | 'draft';
  lifeRemaining?: Record<string, number>;
  note?: string;

  /**
   * Where everyone finished, keyed by pilotId. 1 is the winner; ties share a
   * number and skip the places they consumed.
   *
   * Only games played through the counter have one, because it is built from
   * the order people were eliminated and nothing else in the app witnesses
   * that. Its absence means every loser drew with every other loser, which is
   * how the league scored every game before this existed.
   */
  standing?: Record<string, number>;

  /**
   * What each seat was given before a card was drawn, keyed by pilotId and
   * summing to one.
   *
   * Kept so the model can be marked against reality later: across enough games,
   * seats given 70% should win about 70% of the time. Without this the odds are
   * decoration — a claim nobody can check, because the ratings that produced it
   * have moved on by the time anyone looks.
   */
  predicted?: Record<string, number>;

  /**
   * Life changes over the course of the game, credited to whoever had priority.
   *
   * `from` is an inference, not a fact — life left this player on that player's
   * turn, which is usually but not always why. A board wipe on your own turn
   * costs you life nobody dealt. Kept because a season of it answers questions
   * nothing else can ("who does this pod actually attack?"), and deliberately
   * never shown in the app, because a guess displayed as a fact becomes one.
   *
   * `step` is which individual turn it landed in, as an index into `turnLog` —
   * `turn` says the round and this says whose turn inside it. Absent on games
   * recorded before the counter tracked it, which the chart falls back for.
   *
   * Only recorded for games played through the live counter.
   */
  damageLog?: { to: string; from: string | null; delta: number; turn: number; step?: number }[];

  /**
   * Every pass of the turn, in order: which seat it went to and which round.
   *
   * Kept so the life chart can be read a turn at a time rather than a round at
   * a time. A round is one column for the whole table, which is the wrong
   * resolution for the question people actually ask of it — "what was I on when
   * it came round to me" — and there is no way to recover the answer after the
   * fact, because the rounds do not divide evenly once players start dropping
   * out and their turns stop happening.
   *
   * Seats are indices into `entries`, which is the order they sat in. Absent on
   * games that were typed in rather than played through the counter.
   */
  turnLog?: { seat: number; turn: number }[];

  /**
   * Who knocked whom out, as killer keyed by victim.
   *
   * Confirmed by the table rather than inferred: the app knows a life total hit
   * zero and has no idea whose spell did it. Anybody nobody credited is simply
   * absent — a concede, a board wipe that caught its own caster, a walk into a
   * commander — and that absence is honest rather than a gap to be filled in.
   */
  takedowns?: Record<string, string>;

  /**
   * Poison counters on each player at the end, keyed by pilotId.
   *
   * Final totals rather than a history: poison only goes up, so the total is
   * the whole story, and unlike life it does not need a timeline to be read.
   */
  poison?: Record<string, number>;

  /**
   * What the peanut gallery said about this game, kept.
   *
   * Written when the closing beat lands, which is after the match exists. The
   * feed otherwise lives on the live game and the live game is swept about half
   * an hour after it finishes - so the funniest beat of the night, the one
   * where the bets settle and somebody insists they called it, was being
   * written and then thrown away without anybody able to read it.
   *
   * The whole game's feed, not only the closing, because the record of a night
   * is the whole conversation.
   */
  commentary?: CommentaryPost[];

  /** Poison as it was added, credited the same way the damage log is. */
  poisonLog?: { to: string; from: string | null; delta: number; turn: number }[];

  /**
   * How long the game actually took, in seconds of wall clock.
   *
   * Turn count says how long a game was in game terms; this says how long an
   * evening was in real ones, which is the number that decides whether there is
   * time for another. Only games played through the counter have it, and it
   * measures from the first life total to the moment somebody confirmed the
   * winner — so a pod that left the counter running through a pizza break will
   * say so. Recorded rather than corrected: an honest long number is more
   * useful than a guess at what should be subtracted.
   */
  durationSeconds?: number;

  /**
   * Commander damage at the end: who took it, from whose commander, how much.
   *
   * Kept per source rather than summed, because that is the rule — twenty-one
   * from one commander kills and twenty from each of three does not — and a
   * total would throw away the only thing that makes it different from life.
   */
  commanderDamage?: Record<string, Record<string, number>>;

  deltas: MatchDeltas;

  /**
   * Who crossed out of placements on this match. Recorded at resolve time so the
   * reveal animation can fire even if the client that logged it was offline.
   */
  revealed: { pilots: string[]; decks: string[] };

  /** Client-generated, used to make offline queue replay idempotent. */
  clientId: string;
}

export interface Session {
  id: string;
  ts: number;
  label: string;
}

export interface LeagueSettings {
  /** What this group calls itself. Leaders can change it. */
  guildName: string;

  /**
   * The guild's own mark, as a data URI, or null for the app's default.
   *
   * Bigger budget than a member avatar because it is shown large on the
   * sign-in screen, and PNG is allowed so a logo with a transparent
   * background does not come back sitting on a black square.
   */
  guildIcon: string | null;

  /**
   * On, against the spec's advice to ship it disabled — a deliberate call for
   * this league. The curve was softened to match (see marginMultiplier): a
   * blowout is worth a quarter more, not half again.
   *
   * Only has an effect when a match records the winner's remaining life; with
   * no life recorded the multiplier stays neutral.
   */
  marginOfVictory: boolean;

  /**
   * A rating bonus for playing, worth more at the bottom of the ladder than the
   * top, and paid relative to wherever the guild currently sits.
   *
   * On, because a pool this small is a closed loop without it — the same few
   * people trade the same points forever and an ordinary member's rating is a
   * flat line with noise on it. Averages a rank per twenty games, which is
   * thirty or forty hours of playing.
   *
   * The cost is that ratings inflate, so they only compare within a season and
   * partly measure attendance, and that the curve pulls the ladder slightly
   * together — see the note on participationBonus for how much and why that is
   * the number to watch.
   */
  progressBonus: boolean;
  seasonGameTarget: number;
  seasonStartedAt: number;
}

export const MAX_GUILD_NAME = 32;

export const DEFAULT_SETTINGS: LeagueSettings = {
  guildName: 'Misadventures',
  guildIcon: null,
  marginOfVictory: true,
  progressBonus: true,
  seasonGameTarget: 40,
  seasonStartedAt: 0,
};
